import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useComparisonDetail } from "@/lib/rehearsalQueries";
import ClientShell from "./ClientShell";

const METRIC_ROWS = [
  { key: "duration_seconds", label: "Duration (s)", round: true },
  { key: "overall_wpm", label: "Words per minute", round: true },
  { key: "pause_count", label: "Pauses" },
  { key: "pause_total_duration_seconds", label: "Total pause time (s)", round: true },
  { key: "filler_count", label: "Filler words" },
  { key: "repetition_count", label: "Repetitions" },
];

/**
 * Master v5 Prompt 24 — before/after RehearsalComparison. Reuses the
 * comparison_view renderer concept at the data/registry level (read-only,
 * supports_versions): this page never renders through RoomShell's
 * single-target launch chrome since comparing two independent Rehearsals
 * doesn't fit RoomLaunch's one-target model, but the same rule applies —
 * plain numeric deltas from supported evidence only, never a qualitative
 * verdict, and an honest "not enough evidence" note per side when a row
 * isn't valid.
 */
export default function RehearsalComparisonPage() {
  const { workspaceId, cmpId } = useParams();
  const navigate = useNavigate();
  const { data, isPending, error } = useComparisonDetail(workspaceId, cmpId);

  useEffect(() => {
    if (error?.status === 401) navigate("/rehearsal-room/login", { replace: true });
  }, [error, navigate]);

  if (isPending) {
    return <ClientShell><div className="flex items-center gap-2 text-ink-soft" data-testid="rehearsal-comparison-loading"><Loader2 size={16} className="animate-spin" /> Loading comparison…</div></ClientShell>;
  }
  if (error) {
    if (error.status === 401) return null;
    return <ClientShell><div className="text-terracotta text-sm" data-testid="rehearsal-comparison-error">{error.message}</div></ClientShell>;
  }

  const { baseline_metrics: baseline, latest_metrics: latest, deltas } = data;

  return (
    <ClientShell>
    <div className="max-w-3xl mx-auto space-y-6" data-testid="rehearsal-comparison-page">
      <button onClick={() => navigate(-1)} className="text-ink-soft text-sm flex items-center gap-1.5" data-testid="rehearsal-comparison-back">
        <ArrowLeft size={14} /> Back
      </button>
      <p className="font-serif text-2xl text-ink">Before &amp; after</p>

      <div className="grid grid-cols-3 gap-2 text-sm">
        <p className="text-ink-soft">Measurement</p>
        <p className="text-ink-soft text-center">Before</p>
        <p className="text-ink-soft text-center">After</p>
        {METRIC_ROWS.map(({ key, label, round }) => (
          <ComparisonRow key={key} label={label} baseline={baseline} latest={latest} field={key} round={round} delta={deltas?.[key]} />
        ))}
      </div>

      {(!baseline || baseline.reliability_status !== "valid") && (
        <Note text={`Before: not enough evidence to measure reliably (${(baseline?.reliability_reasons || []).join(", ") || "unavailable"}).`} />
      )}
      {(!latest || latest.reliability_status !== "valid") && (
        <Note text={`After: not enough evidence to measure reliably (${(latest?.reliability_reasons || []).join(", ") || "unavailable"}).`} />
      )}
    </div>
    </ClientShell>
  );
}

function ComparisonRow({ label, baseline, latest, field, round, delta }) {
  const fmt = (m) => {
    if (!m || m.reliability_status !== "valid" || m[field] == null) return "—";
    return round ? Math.round(m[field]) : m[field];
  };
  return (
    <>
      <p className="text-ink py-1.5" data-testid={`rehearsal-comparison-label-${field}`}>{label}</p>
      <p className="text-ink text-center py-1.5" data-testid={`rehearsal-comparison-baseline-${field}`}>{fmt(baseline)}</p>
      <p className="text-ink text-center py-1.5" data-testid={`rehearsal-comparison-latest-${field}`}>
        {fmt(latest)}
        {delta != null && <span className="text-xs text-ink-soft ml-1" data-testid={`rehearsal-comparison-delta-${field}`}>{" "}({delta > 0 ? "+" : ""}{delta})</span>}
      </p>
    </>
  );
}

function Note({ text }) {
  return <p className="text-xs text-ink-soft bg-ink/5 rounded-lg p-3" data-testid="rehearsal-comparison-note">{text}</p>;
}
